import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Trophy, Users, User, CalendarDays, BarChart2, Home, Settings, UserRound, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import UserMenu from "./UserMenu";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import ThemeSwitcher from "@/components/settings/ThemeSwitcher";

const Navigation = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { isStickyHeaderEnabled } = useSettings();

  const navItems = [
    { name: "Home", path: "/", icon: <Home className="h-5 w-5 md:mr-2" /> },
    { name: "Tournaments", path: "/tournaments", icon: <Trophy className="h-5 w-5 md:mr-2" />, requiresAuth: true },
    { name: "Teams", path: "/teams", icon: <Users className="h-5 w-5 md:mr-2" />, requiresAuth: true },
    { name: "Players", path: "/players", icon: <User className="h-5 w-5 md:mr-2" />, requiresAuth: true },
    { name: "Matches", path: "/matches", icon: <CalendarDays className="h-5 w-5 md:mr-2" />, requiresAuth: true },
    { name: "Statistics", path: "/statistics", icon: <BarChart2 className="h-5 w-5 md:mr-2" />, requiresAuth: true },
  ];

  return (
    <header className={cn("bg-background border-b z-30 w-full shadow-sm", isStickyHeaderEnabled && "sticky top-0")}>
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-bold text-xl text-cricket-green hidden sm:block">
              Cricket Scoring
            </span>
          </Link>

          <nav className="hidden sm:flex items-center gap-2">
            {navItems.map((item) => {
              // Skip auth required items if user is not logged in
              if (item.requiresAuth && !user) return null;
              
              return (
                <Button
                  key={item.path}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "flex flex-col md:flex-row items-center",
                    location.pathname === item.path && "bg-accent"
                  )}
                  asChild
                >
                  <Link to={item.path}>
                    {item.icon}
                    <span className="text-xs md:text-sm">{item.name}</span>
                  </Link>
                </Button>
              );
            })}
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <ThemeSwitcher />
          <UserMenu />
        </div>
      </div>

      <nav className="sm:hidden bg-background border-t fixed bottom-0 left-0 w-full z-30 pb-safe">
        <div className="container flex justify-between items-center h-16 px-2">
          {navItems.map((item) => {
            // Skip auth required items if user is not logged in
            if (item.requiresAuth && !user) return null;
            
            return (
              <Button
                key={item.path}
                variant="ghost"
                size="sm"
                className={cn(
                  "flex-1 flex flex-col items-center justify-center py-1 h-full min-h-0",
                  location.pathname === item.path
                    ? "bg-accent text-cricket-green"
                    : "text-gray-500"
                )}
                asChild
              >
                <Link to={item.path}>
                  {item.icon}
                  <span className="text-xs mt-0.5 leading-tight">{item.name}</span>
                </Link>
              </Button>
            );
          })}
        </div>
      </nav>
    </header>
  );
};

export default Navigation;
